import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDate as formatDatePt } from '../../utils/date';
import { getProductsByCode, ProductLoteShelfDto } from '../../services/expensesApi';

const ProductCodeList: React.FC = () => {
  const [productsByCode, setProductsByCode] = useState<Record<string, ProductLoteShelfDto[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProductsByCode = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProductsByCode();
      setProductsByCode(data);
    } catch (e: any) {
      setError(e.message || 'Não foi possível carregar os produtos por código');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchProductsByCode(); 
  }, []);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/D';
    return formatDatePt(dateString);
  };

  const getExpirationColor = (dateString?: string) => {
    if (!dateString) return 'default';
    const expDate = new Date(dateString);
    const today = new Date();
    const diffDays = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    
    if (diffDays < 0) return 'error'; // Expired
    if (diffDays <= 7) return 'warning'; // Expires soon
    if (diffDays <= 30) return 'info'; // Expires in a month
    return 'success'; // Good expiration
  };

  if (loading) {
    return <Typography>A carregar produtos...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const productCodes = Object.keys(productsByCode);

  if (productCodes.length === 0) {
    return <Typography>Nenhum produto encontrado com código.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Produtos por código ({productCodes.length} códigos de produto)
      </Typography>
      
      {productCodes.map((productCode) => {
        const lotes = productsByCode[productCode];
        const totalQuantity = lotes.reduce((sum, lote) => sum + lote.quantity, 0);
        const productName = lotes[0]?.productName || 'Produto desconhecido';
        
        return (
          <Accordion key={productCode} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Typography variant="h6">{productCode}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {productName}
                  </Typography>
                </Box>
                <Chip 
                  label={`${lotes.length} lotes`} 
                  size="small" 
                  color="primary" 
                />
                <Chip 
                  label={`Total: ${totalQuantity} unidades`} 
                  size="small" 
                  color="secondary" 
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Prateleira</TableCell>
                      <TableCell>Número do lote</TableCell>
                      <TableCell>Quantidade</TableCell>
                      <TableCell>Data de validade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lotes.map((lote) => (
                      <TableRow key={lote.publicId}>
                        <TableCell>{lote.shelfName}</TableCell>
                        <TableCell>{lote.loteNumber || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={lote.quantity} 
                            size="small" 
                            color={lote.quantity > 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatDate(lote.expirationDate)} 
                            size="small" 
                            color={getExpirationColor(lote.expirationDate)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default ProductCodeList;
